import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLotImage } from '../../utils/getLotImage';
import { useOperationMsData } from './hooks/useOperationMsData';
import { useOperationMsTabs } from './hooks/useOperationMsTabs';
import { useUnreadCounts } from './hooks/useUnreadCounts';
import OperationHeader from './components/OperationHeader';
import OperationTabs from './components/OperationTabs';
import OperationContent from './components/OperationContent';
import './SuiviOperationMSV2.css';

// Skeleton plein ecran (chargement) : silhouette banniere + onglets + contenu, dans les MEMES
// conteneurs (.opms / .opms__content / .opms__tab-content) et avec la meme geometrie que le vrai
// rendu (banniere OperationHeaderBanner) -> aucun saut de layout. Purement decoratif.
const OperationMSSkeleton: React.FC = () => (
  <div className="opms opms-skeleton" aria-hidden="true">
    <div className="opms-sk-banner">
      <div className="opms-sk-back"><span className="opms-sk opms-sk-circle" /></div>
      <div className="opms-sk-h1"><span className="opms-sk" /></div>
      <div className="opms-sk-h2"><span className="opms-sk" /></div>
      <div className="opms-sk-subtitle"><span className="opms-sk" /></div>
      <div className="opms-sk-statusline"><span className="opms-sk" /></div>
    </div>
    <div className="opms__content">
      <div className="opms-sk-tabs">
        {Array.from({ length: 7 }).map((_, i) => (
          <span key={i} className="opms-sk opms-sk-tab" />
        ))}
      </div>
      <div className="opms__tab-content">
        <div className="opms-sk-content">
          <span className="opms-sk opms-sk-block" />
          <span className="opms-sk opms-sk-block" />
          <span className="opms-sk opms-sk-block opms-sk-block--lg" />
        </div>
      </div>
    </div>
  </div>
);

// Page acheteur Operation MS (V2) : data -> onglets -> rendu (orchestration mince, ACHETEUR SEUL).
const SuiviOperationMSV2: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { operation, loading, error, refetch, setOperation } = useOperationMsData(id);
  const lotImage = useLotImage(operation?.lot_technique?.nom);
  const { descriptors, activeIndex, activeTab, setActiveIndex } = useOperationMsTabs(operation);
  const { counts: unreadCounts, markRead } = useUnreadCounts(operation?.id);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  // Efface le badge du canal des qu'on active son onglet (couvre la race fetch/changement d'onglet).
  const activeKind = activeTab?.kind;
  useEffect(() => {
    if (activeKind === 'questions_reponses' || activeKind === 'messagerie') markRead(activeKind);
  }, [activeKind, unreadCounts, markRead]);

  if (loading) return <OperationMSSkeleton />;
  if (error && !operation) return <div className="opms opms__error">Erreur : {error}</div>;
  if (!operation) return <div className="opms opms__empty">Aucune opération trouvée.</div>;

  return (
    <div className="opms opms__shell" ref={contentRef}>
      <div className="no-print">
        <OperationHeader operation={operation} lotImage={lotImage} />
      </div>

      <div className="opms__content">
        <OperationTabs
          descriptors={descriptors}
          activeIndex={activeIndex}
          onSelect={setActiveIndex}
          unreadCounts={unreadCounts}
        />
        <div className="opms__tab-content">
          <OperationContent
            operation={operation}
            setOperation={setOperation}
            activeTab={activeTab}
            errors={errors}
            setErrors={setErrors}
            refetch={refetch}
            onMarkRead={markRead}
            printRef={contentRef}
          />
        </div>
      </div>
    </div>
  );
};

export default SuiviOperationMSV2;
